import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { UserStoreService } from 'src/app/services/user.store';
import { AuthStoreService } from 'src/app/services/auth.store';
import { ENTRY_CLAIM_PATH, EntryIntentService } from 'src/app/services/entry-intent.service';
import { isNormalUseUserStatus } from '../interfaces';

// Redirects fully-activated logged-in users away from the home page:
//   Biz/fans admin      → /business-admin
//   B2B community user  → /ask
//   B2C with teams      → /ask
//   B2C without teams   → /start/claim when an entry-funnel selection is pending, else /teams
// Users with non-activated status (e.g. reset-password, inactive) stay on /home.
export const HomeGuard: CanActivateFn = async () => {
  const userStore = inject(UserStoreService);
  const authStore = inject(AuthStoreService);
  const entryIntent = inject(EntryIntentService);
  const router = inject(Router);

  // Wait for the boot-time community fetch to settle too — the isBizFansUser()/isConsumer()
  // checks below read authStore.community()/communitySubdomain()-derived state, which on a
  // hard refresh can still be mid-flight if we only waited on user status (see 2026-08-11
  // /teams -> /home -> /ask bounce investigation).
  const [userStatus] = await Promise.all([
    userStore.getUserStatusAsync(),
    authStore.getCommunityStateSettledAsync()
  ]);
  const isActivated = isNormalUseUserStatus(userStatus);

  if (!userStore.loggedIn() || !isActivated) {
    return true;
  }

  // Logged-in biz/fans admin → go to business admin portal
  // Use isBizFansAdmin() (communitySubdomain()-derived) rather than subdomain() (current URL) —
  // subdomain() is 'auth' while on the auth subdomain itself, so it never matches 'biz'/'fans'
  // there even though communitySubdomain() (awaited above via getCommunityStateSettledAsync())
  // already holds the admin's actual business subdomain.
  if (authStore.isBizFansAdmin()) {
    return router.navigate(['/business-admin']);
  }

  // Logged-in B2B community user → always go to /ask (teams are community-assigned)
  if (authStore.isBizFansUser()) {
    return router.navigate(['/ask']);
  }

  // Logged-in B2C consumer
  if (authStore.isConsumer()) {
    const user = userStore.user();
    if (user?.UserTeams && user.UserTeams.length > 0) {
      return router.navigate(['/ask']);
    } else if (entryIntent.get()) {
      // Entry-funnel safety net: a pending team selection (e.g. a cookie-restored session that
      // never reached /start/claim) is claimed instead of sending the user to the full catalog.
      return router.navigate([ENTRY_CLAIM_PATH]);
    } else {
      return router.navigate(['/teams']);
    }
  }

  return true;
};
